import { useMemo } from "react";
import { OutlineButton, Modal, TextButton, PrimaryButton, InputDropdown, InputDate } from "@artprov/design-system";
import { useModalFilter } from "../hooks/useModalFilter.js";

export default function ModalFilter() {
    const {
        // Estados
        isFilterModalOpen,
        setIsFilterModalOpen,
        resetKey,
        bancos,
        estados,
        bancosLoading,
        estadosLoading,
        bancosError,
        estadosError,

        // Estados del formulario
        bancoSelected,
        bancoSelectedLabel,
        fechaDesdeInput,
        fechaHastaInput,
        fechaEstadoDesdeInput,
        fechaEstadoHastaInput,
        estadoSelected,
        estadoSelectedLabel,
        errorFechaDesde,
        errorFechaHasta,
        errorFechaEstadoDesde,
        errorFechaEstadoHasta,
        warningFechaDesde,

        // Handlers
        handleAccept,
        handleClear,
        handleBancoChange,
        handleEstadoChange,
        handleFechaDesdeChange,
        handleFechaHastaChange,
        handleFechaEstadoDesdeChange,
        handleFechaEstadoHastaChange,
        restaurarFiltrosAplicados,

    } = useModalFilter();

    // Opciones para dropdowns - Memoizadas para evitar re-renders innecesarios
    const opcionesBanco = useMemo(() => {
        if (bancosLoading) {
            return [
                <InputDropdown.Option key="loading-banco" value="" disabled>
                    Cargando bancos...
                </InputDropdown.Option>
            ];
        }

        if (bancosError) {
            return [
                <InputDropdown.Option key="error-banco" value="" disabled>
                    Error al cargar bancos
                </InputDropdown.Option>
            ];
        }

        if (bancos.length === 0) {
            return [
                <InputDropdown.Option key="empty-banco" value="" disabled>
                    No hay bancos disponibles
                </InputDropdown.Option>
            ];
        }

        return bancos.map((banco) => (
            <InputDropdown.Option
                key={`banco-${banco.id}`}
                id={`banco-${banco.id}`}
                selected={bancoSelected === banco.value}
                value={banco.value.toString()}
            >
                {banco.label}
            </InputDropdown.Option>
        ));
    }, [bancos, bancosLoading, bancosError, bancoSelected]);

    const opcionesEstado = useMemo(() => {
        if (estadosLoading) {
            return [
                <InputDropdown.Option key="loading-estado" value="" disabled>
                    Cargando estados...
                </InputDropdown.Option>
            ];
        }

        if (estadosError) {
            return [
                <InputDropdown.Option key="error-estado" value="" disabled>
                    Error al cargar estados
                </InputDropdown.Option>
            ];
        }

        if (estados.length === 0) {
            return [
                <InputDropdown.Option key="empty-estado" value="" disabled>
                    No hay estados disponibles
                </InputDropdown.Option>
            ];
        }

        return estados.map((estado) => (
            <InputDropdown.Option
                key={`estado-${estado.value}`}
                id={`estado-${estado.value}`}
                selected={estadoSelected === estado.value}
                value={estado.value.toString()}
            >
                {estado.label}
            </InputDropdown.Option>
        ));
    }, [estados, estadosLoading, estadosError, estadoSelected]);

    // Validaciones
    const hasErrors = errorFechaDesde || errorFechaHasta || errorFechaEstadoDesde || errorFechaEstadoHasta;
    const isAcceptDisabled = hasErrors;
    const isClearDisabled = !bancoSelected && !fechaDesdeInput && !fechaHastaInput && !fechaEstadoDesdeInput && !fechaEstadoHastaInput && !estadoSelected;

    const handleCloseModal = () => {
        restaurarFiltrosAplicados();
        setIsFilterModalOpen(false);
    };

    return (
        <div>
            <OutlineButton
                variant="default"
                className="border-2 rounded-[4px] hover:border-2 hover:rounded-[4px]"
                size="small"
                IconLeft="filter_list"
                onClick={() => setIsFilterModalOpen(true)}
            >
                Filtros
            </OutlineButton>

            {isFilterModalOpen && (
                <Modal.Container
                    open={isFilterModalOpen}
                    setOpen={setIsFilterModalOpen}
                    closeOnClickOutside={false}
                    onClose={handleCloseModal}
                >
                    <Modal.Content height="auto" width="640px" className="py-5 px-5">
                        <Modal.Header>
                            <Modal.Title>Filtros</Modal.Title>
                            <Modal.Close onClose={handleCloseModal} />
                        </Modal.Header>

                        <div className="flex-grow">
                            <div className="flex flex-col gap-1 w-full">
                                <InputDropdown
                                    key={`banco-${resetKey}`}
                                    label="Banco"
                                    name="banco"
                                    id="banco"
                                    value={bancoSelectedLabel}
                                    placeholder="Seleccionar banco"
                                    status="default"
                                    position="bottom"
                                    onChange={({ value }) => handleBancoChange(value)}
                                    maxHeight="300px"
                                    defaultSelection={false}
                                    disabled={bancosLoading}
                                >
                                    {opcionesBanco}
                                </InputDropdown>

                                <div className="flex gap-4">
                                    <InputDate
                                        label="Fecha mail desde"
                                        value={fechaDesdeInput}
                                        onChange={handleFechaDesdeChange}
                                        hints={[
                                            ...(errorFechaDesde && errorFechaDesde !== "Ingresá una fecha válida"
                                            ? [{ variant: 'error', text: errorFechaDesde }]
                                            : []),
                                            ...(warningFechaDesde ? [{ variant: 'info', text: warningFechaDesde }] : [])
                                        ]}
                                        calendarRenderMode="portal"
                                        className="flex-1"
                                    />
                                    <InputDate
                                        label="Fecha mail hasta"
                                        value={fechaHastaInput}
                                        onChange={handleFechaHastaChange}
                                        hints={[
                                            ...(errorFechaHasta && errorFechaHasta !== "Ingresá una fecha válida"
                                            ? [{ variant: 'error', text: errorFechaHasta }]
                                            : [])
                                        ]}
                                        calendarRenderMode="portal"
                                        className="flex-1"
                                    />
                                </div>

                                <div className="flex gap-4">
                                    <InputDate
                                        label="Fecha estado desde"
                                        value={fechaEstadoDesdeInput}
                                        onChange={handleFechaEstadoDesdeChange}
                                        hints={[
                                            ...(errorFechaEstadoDesde && errorFechaEstadoDesde !== "Ingresá una fecha válida"
                                            ? [{ variant: 'error', text: errorFechaEstadoDesde }]
                                            : [])
                                        ]}
                                        calendarRenderMode="portal"
                                        calendarPosition="top"
                                        className="flex-1"
                                    />
                                    <InputDate
                                        label="Fecha estado hasta"
                                        value={fechaEstadoHastaInput}
                                        onChange={handleFechaEstadoHastaChange}
                                        hints={[
                                            ...(errorFechaEstadoHasta && errorFechaEstadoHasta !== "Ingresá una fecha válida"
                                            ? [{ variant: 'error', text: errorFechaEstadoHasta }]
                                            : [])
                                        ]}
                                        calendarRenderMode="portal"
                                        calendarPosition="top"
                                        className="flex-1"
                                    />
                                </div>

                                <InputDropdown
                                    key={`estado-${resetKey}`}
                                    label="Estado"
                                    name="estado"
                                    id="estado"
                                    value={estadoSelectedLabel}
                                    placeholder="Seleccionar estado"
                                    status="default"
                                    position="bottom"
                                    onChange={({ value }) => handleEstadoChange(value)}
                                    maxHeight="300px"
                                    defaultSelection={false}
                                    disabled={estadosLoading}
                                >
                                    {opcionesEstado}
                                </InputDropdown>
                            </div>
                        </div>

                        <Modal.Footer className="flex justify-end">
                            <div className="flex  max-h-9">
                                <TextButton
                                    className="mx-2"
                                    size="small"
                                    onClick={handleClear}
                                    disabled={isClearDisabled}
                                >
                                    Borrar filtros
                                </TextButton>
                                <PrimaryButton
                                    className="ml-2"
                                    size="small"
                                    onClick={handleAccept}
                                    classNameIcon="text-tertiary-100"
                                    disabled={isAcceptDisabled}
                                >
                                    Ver resultados
                                </PrimaryButton>
                            </div>
                        </Modal.Footer>
                    </Modal.Content>
                </Modal.Container>
            )}
        </div>
    );
}
